import { lazy, useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import "assets/styles/index.scss";
import { GameProvider } from "shared/context/gameContext";
import { HintProvider } from "shared/context/hintContext";
import { ConfigProvider, useConfig } from "./config";

import VideoAdPlayer from "ad/videoAdSDK";

const Sudoku = lazy(() => import("games/sudoku"));
const GridPuzzle = lazy(() => import("games/gridPuzzle"));
const WordSearch = lazy(() => import("games/wordSearch"));
const CrossWord = lazy(() => import("games/crossWord"));
const Home = lazy(() => import("./home"));

function reorderDailyPuzzles(ids = []) {
  const dayNumber = new Date().getDay();
  return ids.slice(dayNumber).concat(ids.slice(0, dayNumber));
}

const AppContent = () => {
  const [loading, setLoading] = useState(true);
  const { config } = useConfig();

  const hintHandlerRef = useRef(null);
  const [percentage, setPercentage] = useState("0%");
  const [timer, setTimer] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const [resetGame, setResetGame] = useState(0);

  const activeGame = useLocation().pathname.split("/")[1] || "sudoku";

  const gameCategories = config.games.reduce((p, c) => {
    p[c.type] = c.categories;
    return p;
  }, {});

  const [selectedCategory, setSelectedCategory] = useState(
    gameCategories[activeGame][0]
  );
  const [levelIds, setLevelIds] = useState([]);

  const [selectedLevel, setSelectedLevel] = useState(0);
  const [gameData, setGameData] = useState(null);
  const [lastShownAd, setLastShownAd] = useState(null);

  useEffect(() => {
    if (!isPaused) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => {
      clearInterval(timerRef.current);
    };
  }, [isPaused]);

  const onHintClick = () => {
    if (hintHandlerRef.current) {
      // call the hint handler
      hintHandlerRef.current();
    }
  };

  const onResetGame = () => {
    setResetGame((prev) => prev + 1);
  };

  const gameTypes = gameCategories[activeGame];

  useEffect(() => {
    fetch(
      `/game/${activeGame}/${selectedCategory.type}?limit=${selectedCategory.levelsCount}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (selectedCategory.showLevelDate) {
          const reorderedLevels = reorderDailyPuzzles(data.data);
          setLevelIds(reorderedLevels);
          setSelectedLevel(reorderedLevels[0]);
        } else {
          setLevelIds(data.data);
          setSelectedLevel(data.data[0]);
        }
      })
      .catch((error) => {
        console.error("Error fetching levelids:", error);
        setGameData(null);
      });
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedLevel) {
      fetch(`/game/${activeGame}/${selectedCategory.type}/${selectedLevel}`)
        .then((res) => res.json())
        .then((data) => {
          setGameData(data.data);
          onResetGame();
        })
        .catch((error) => {
          console.error("Error fetching game data:", error);
          setGameData(null);
        });
    }
  }, [selectedLevel]);

  useEffect(() => {
    setLoading(!gameData || !selectedLevel);
  }, [gameData, selectedLevel]);

  function hideAd() {
    setSelectedLevel((s) => {
      setLastShownAd(s);
      return s;
    });
  }

  return (
    <HintProvider>
      {selectedLevel != lastShownAd && (
        <VideoAdPlayer
          onAdCompleted={() => hideAd()}
          onError={() => hideAd()}
          onNoAd={() => hideAd()}
        />
      )}
      <Home
        gameCount={levelIds.length}
        createDate={gameData?.createDate}
        setSelectedLevel={setSelectedLevel}
        selectedLevel={selectedLevel}
        levelTitle={gameData?.title}
        levelId={selectedLevel}
        types={gameTypes}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        percentage={percentage}
        onHintClick={onHintClick}
        timer={timer}
        isPaused={isPaused}
        setIsPaused={setIsPaused}
        onResetGame={onResetGame}
        setTimer={setTimer}
        levelIds={levelIds}
      >
        {!loading && (
          <GameProvider>
            <Routes>
              <Route
                path="/sudoku/:id"
                element={
                  <Sudoku
                    mockData={gameData}
                    key={resetGame}
                    percentage={percentage}
                    setPercentage={setPercentage}
                    hintHandlerRef={hintHandlerRef}
                    setIsPaused={setIsPaused}
                  />
                }
              />
              <Route
                path="/gridpuzzle/:id"
                element={
                  <GridPuzzle
                    mockData={gameData}
                    key={resetGame}
                    percentage={percentage}
                    setPercentage={setPercentage}
                    hintHandlerRef={hintHandlerRef}
                    setIsPaused={setIsPaused}
                  />
                }
              />
              <Route
                path="/wordsearch/:id"
                element={
                  <WordSearch
                    mockData={gameData}
                    key={resetGame}
                    percentage={percentage}
                    setPercentage={setPercentage}
                    hintHandlerRef={hintHandlerRef}
                    setIsPaused={setIsPaused}
                  />
                }
              />
              <Route
                path="/crossword/:id"
                element={
                  <CrossWord
                    mockData={gameData}
                    key={resetGame}
                    percentage={percentage}
                    setPercentage={setPercentage}
                    hintHandlerRef={hintHandlerRef}
                    setIsPaused={setIsPaused}
                    id={selectedLevel}
                  />
                }
              />
              <Route path="*" element={<Navigate to="/sudoku/play" />} />
            </Routes>
          </GameProvider>
        )}
      </Home>
    </HintProvider>
  );
};

const App = () => {
  return (
    <ConfigProvider>
      <AppContent />
    </ConfigProvider>
  );
};

export default App;
